import React, { useEffect, useCallback } from 'react';
import { usePortals } from 'react-portal-hook';
import { SvgIcon, TooltipDisclaimer } from '~/common/components/ui-elements';
import {
  getRepayments,
  getFinanceFormInput,
  openFinanceModal,
  closeFinanceModal,
  getFinanceFormInputPersonalised,
} from '~/common/services/finance-service';
import { usePricingQueue } from '~/common/contexts/PricingQueueContext';
import { usePostcode } from '~/common/hooks/use-postcode';
import { ModelDetailPricing } from '~/common/components';
import { GenericModal } from '~/common/components/generic-modal';
import ImgLazyLoad from '~/common/components/image-lazy-loading';
import { ShowroomModelV3 } from '../../store';
import { LocationDetails } from '../location-details';
import { EnterPostCode } from '../enter-postcode';
import styles from './index.module.scss';
import { PriceDetails } from '../price-details';

interface Props {
  model: ShowroomModelV3;
  size: 'half' | 'full';
}

export const VehicleCardV3Component: React.FC<Props> = ({ model, size }) => {
  const portalManager = usePortals();
  const { postcode } = usePostcode();
  const { registerVehicle, getPricingState, globalSuburb } = usePricingQueue();

  useEffect(() => {
    registerVehicle({
      id: model.id,
      pimIdentifier: model.pimIdentifier,
      additionalOptionsPrice: model.additionalOptionsPrice,
    });
  }, [
    model.id,
    model.pimIdentifier,
    model.additionalOptionsPrice,
    registerVehicle,
  ]);

  const { priceInfo, repaymentInfo, isLoading, isError } = getPricingState(
    model.id
  );
  const suburb = globalSuburb;

  const handleShowPostCode = () => {
    window.dispatchEvent(new CustomEvent('selectLocation', { detail: {} }));
  };

  const handleShowFinanceModal = () => {
    openFinanceModal(model.pimIdentifier, postcode);
  };

  const handleShowPrice = () => {
    portalManager.open((portal) => (
      <ModelDetailPricing
        modelName={model.model}
        mlp={priceInfo?.mlp as number}
        rdp={priceInfo?.rdp as number}
        closeModal={portal.close}
        pimIdentifier={model.pimIdentifier}
        rdpDisclaimerOverride={model.rdpDisclaimerOverride}
      />
    ));
  };

  const handleShowFinanceDisclaimer = (element: any, title: string) => {
    portalManager.open((portal) => (
      <GenericModal title={title} element={element} closeModal={portal.close} />
    ));
  };

  return (
    <div
      className={`${styles.vehicleCard} ${styles[size]}`}
      id={model.linkName}
    >
      <div className={styles.imageContainer}>
        <ImgLazyLoad loading="lazy" src={model.modelImage} alt={model.name} />
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <div className={styles.badgeImage}>
            <ImgLazyLoad
              loading="lazy"
              src={model.modelImageName}
              alt={model.model}
            />
          </div>
          {model.displayModelPrice &&
            (suburb && priceInfo && repaymentInfo ? (
              <LocationDetails
                suburb={suburb}
                price={priceInfo.rdp}
                showPostCode={handleShowPostCode}
                showPriceDetails={handleShowPrice}
              />
            ) : (
              <EnterPostCode
                error={isError}
                loading={isLoading}
                showPostCode={handleShowPostCode}
              />
            ))}
        </div>

        {suburb && priceInfo && repaymentInfo && model.displayModelPrice && (
          <PriceDetails
            price={priceInfo.rdp}
            showPostCode={handleShowPostCode}
            showPriceDetails={handleShowPrice}
            loading={isLoading}
            repaymentInfo={repaymentInfo}
            showFinanceModal={handleShowFinanceModal}
            isFinanceFormInputPersonalised={getFinanceFormInputPersonalised()}
            showFinanceDisclaimer={handleShowFinanceDisclaimer}
            suburb={suburb}
            showGFV={!model.hideGfvShowroom}
          />
        )}

        <div
          className={styles.description}
          dangerouslySetInnerHTML={{ __html: model.modelText }}
        />

        <div className={styles.features}>
          <ul>
            {(model.features || []).map((feature, idx) => (
              <li key={idx}>
                <TooltipDisclaimer disclaimer={feature.featureDisclaimer}>
                  {feature.featureName}
                </TooltipDisclaimer>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.ctaContainer}>
          {model.ctaButton && model.ctaButton.length > 0 && (
            <div className={styles.mainCtas}>
              {(model.ctaButton || []).map((btn, idx) => (
                <a
                  key={idx}
                  href={btn.url}
                  target={btn.target || '_self'}
                  className={styles.primaryBtn}
                >
                  {btn.name}
                </a>
              ))}
            </div>
          )}

          <div className={styles.footerCtas}>
            {!model.hideByoCta && (
              <a href="/configure" className={styles.textLink}>
                <SvgIcon type="chevronRight" size="12px" color="#0042a6" />
                <span>Build your own</span>
              </a>
            )}
            {!model.hideInBrochureDownload && (
              <a
                href={`/brochure-download?model=${model.model}`}
                className={styles.textLink}
              >
                <SvgIcon type="chevronRight" size="12px" color="#0042a6" />
                <span>Download brochure</span>
              </a>
            )}
            {!model.hideInTestDrive && (
              <a
                href={`/book-test-drive?model=${model.model}`}
                className={styles.textLink}
              >
                <SvgIcon type="chevronRight" size="12px" color="#0042a6" />
                <span>Request a test drive</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const VehicleCardV3 = React.memo(
  VehicleCardV3Component,
  (prevProps, nextProps) => {
    return (
      prevProps.model.id === nextProps.model.id &&
      prevProps.size === nextProps.size
    );
  }
);
